/**
 * Subscription Domain - Shared Constants
 * 
 * Centralized constants for subscriptions (Memberships and Passes).
 * These constants ensure consistency across the application and provide
 * a single source of truth for subscription-related UI options.
 */

import type { ColorVariant } from '@lgc/database/types';

/**
 * Color options for subscriptions (Memberships and Passes)
 * 
 * These options must match the ColorVariant type from the database schema
 * and the available gradientVariants in the UI components.
 * 
 * @see {@link @lgc/database/types/domains/subscription/base.ColorVariant}
 * @see {@link @butter/ui/primitives/data-display}
 */
export const SUBSCRIPTION_COLOR_OPTIONS: Array<{ 
  name: string; 
  value: ColorVariant;
  description?: string;
}> = [
  { name: "Beige", value: "beige" },
  { name: "Gold", value: "gold" },
  { name: "Diamond", value: "diamond" },
  { name: "Platinum", value: "platinum" },
  { name: "Purple", value: "purple" },
  { name: "Aqua", value: "aqua" },
  { name: "Blue", value: "blue" },
  { name: "Green", value: "green" },
  { name: "Red", value: "red" },
] as const;

/**
 * Default color for subscriptions when no color is specified
 */
export const DEFAULT_SUBSCRIPTION_COLOR: ColorVariant = "purple";

/**
 * Helper to validate if a color is a valid subscription color
 */
export function isValidSubscriptionColor(color: string): color is ColorVariant {
  return SUBSCRIPTION_COLOR_OPTIONS.some(option => option.value === color);
}

/**
 * Helper to get color option by value
 */
export function getColorOption(color: ColorVariant) {
  return SUBSCRIPTION_COLOR_OPTIONS.find(option => option.value === color);
}

/**
 * Helper to get a safe color value (returns default if invalid)
 */
export function getSafeSubscriptionColor(color: string | null | undefined): ColorVariant {
  if (!color) return DEFAULT_SUBSCRIPTION_COLOR;
  return isValidSubscriptionColor(color) ? color : DEFAULT_SUBSCRIPTION_COLOR;
}

// ============================================================================
// ALLOWANCE UNIT
// ============================================================================

/** Which unit a plan's concurrent-allowance limit is expressed in. */
export type AllowanceUnit = 'hours' | 'sessions';

/**
 * Which allowance input a plan should render.
 *
 * **A plan keeps the unit it was created with.** `clubs.usageLimitMode` decides
 * the unit for a plan that has neither limit set — i.e. a NEW plan — and nothing
 * more. Flipping the club toggle does not restate existing plans: a plan created
 * in hours goes on showing (and enforcing) hours forever.
 *
 * That is what makes the toggle safe. Because a plan always renders its own
 * unit, no value is ever hidden while still being enforced, so nothing can be
 * stranded and no "clear the other field" affordance is needed. (The ticket
 * parks that action for product; this rule removes the need for it.)
 *
 * Both-set is unreachable through the UI — only one input renders, so only one
 * field is ever written — and there is no per-field history that could say which
 * came first, so it falls back to the club default. If that state ever appears
 * in real data it means something wrote the columns directly.
 *
 * @param limits   The plan's stored limits. Empty string counts as unset, so
 *                 form state can be passed straight in.
 * @param clubMode `clubs.usageLimitMode` — the default for new plans.
 */
export function resolveAllowanceUnit(
  limits: {
    maxActiveMinutes?: number | string | null;
    maxActiveSessions?: number | string | null;
  },
  clubMode: AllowanceUnit = 'hours',
): AllowanceUnit {
  const isSet = (v: number | string | null | undefined) =>
    v !== null && v !== undefined && String(v).trim() !== '';

  const hasSessions = isSet(limits.maxActiveSessions);
  const hasHours = isSet(limits.maxActiveMinutes);

  if (hasSessions && !hasHours) return 'sessions';
  if (hasHours && !hasSessions) return 'hours';
  return clubMode;
}

// ============================================================================
// ALLOWANCE FIELD COPY
// ============================================================================

/**
 * Copy for the fourth usage-limit cell — the one whose unit
 * `resolveAllowanceUnit` decides.
 *
 * Two components render that cell: the template editors (`UsageLimitsFields`,
 * used by Plans and Passes) and the instance grid (`UsageLimitsGrid`, used by
 * the Users and Teams drawers). Defined here so they cannot drift.
 *
 * The `MAX_` variants exist because the instance grid prefixes all four of its
 * fields ("Max Hours Per Day", …) while the template editor does not ("Hours
 * per Day", …). Each surface stays internally consistent. To collapse that
 * difference, point the grid at the unprefixed constants and rename its three
 * hour fields to match the template editor — a copy decision, not a code one.
 */
export const ACTIVE_HOURS_LABEL = 'Active Hours';
export const MAX_ACTIVE_HOURS_LABEL = 'Max Active Hours';
export const ACTIVE_HOURS_HINT = 'How many hours member can have on the calendar at a time';

export const ACTIVE_SESSIONS_LABEL = 'Active Sessions';
export const MAX_ACTIVE_SESSIONS_LABEL = 'Max Active Sessions';
export const ACTIVE_SESSIONS_HINT = 'How many bookings member can have on the calendar at a time';
